import React, { useState, useEffect, memo, useMemo } from "react";
import { Card, CardContent, CardMedia, Typography, Box, Modal, IconButton, Tooltip } from "@mui/material";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import { getSizeRank } from "../sizeUtils";
import { getProductImageThumbUrl, getProductRealisation } from "../pictorialUtils";

const CHIP_CONTENT_SX = {
	display: "flex",
	alignItems: "center",
	justifyContent: "center",
	textAlign: "center",
	color: "#000",
	fontSize: "0.48rem",
	fontWeight: 500,
	px: 0.75,
	py: 0.25,
	borderRadius: 0.5,
	lineHeight: 1.4,
	letterSpacing: "0.02em",
	whiteSpace: "nowrap",
};

/** Period sell-through: sold in range ÷ buy (inward) qty — matches PictorialST header Period ST. */
const getPeriodStPct = (product) => {
	const buy = Number(product?.troco_ls?.t_buy) || 0;
	const sold = Number(product?.troco_ls?.t_sale) || 0;
	if (buy <= 0) return 0;
	return Math.round((sold / buy) * 10000) / 100;
};

/** SALE.CHANNEL values that map to ECOM aging / ROS bucket (aligned with backend). */
const ECOM_METRICS_CHANNELS = new Set([
	"D2C",
	"D2C-INTERNATIONAL",
	"MP",
	"Marketplace",
	"Marketplace-INTERNATIONAL",
	"MP SOR",
	"MARKETPLACE OUTRIGHT",
	"LIQUIDATION",
	"CORPORATE",
]);

/** Maps API metrics channel to tooltip row key EBO | LFS | MBO | ECOM */
function metricsChannelToBucket(metricsChannel) {
	if (!metricsChannel) return null;
	if (metricsChannel === "EBO" || metricsChannel === "LFS" || metricsChannel === "MBO") return metricsChannel;
	if (ECOM_METRICS_CHANNELS.has(metricsChannel)) return "ECOM";
	return null;
}

/**
 * Scroll smoothly to the product card with the given PKEY and flash an
 * orange highlight ring for 1.8 s so the user can spot it instantly.
 */
function scrollToProductCard(pkey) {
	const id = `pict-card-${String(pkey).replace(/[^a-zA-Z0-9]/g, "-")}`;
	const el = document.getElementById(id);
	if (!el) return;
	el.scrollIntoView({ behavior: "smooth", block: "center" });
	el.style.transition = "outline 0.15s ease, box-shadow 0.15s ease";
	el.style.outline = "3px solid #e65100";
	el.style.borderRadius = "8px";
	el.style.boxShadow = "0 0 0 5px rgba(230,81,0,0.25)";
	setTimeout(() => {
		el.style.outline = "";
		el.style.boxShadow = "";
		el.style.borderRadius = "";
	}, 1800);
}

/**
 * Given a season like "AW-26" or "SS-26", return the ordered list of preceding
 * seasons to check as fallbacks.
 *   AW-26 → [SS-26, AW-25, SS-25, AW-24, SS-24, ...]
 *   SS-26 → [AW-25, SS-25, AW-24, SS-24, ...]
 */
function getSeasonFallbacks(season, max = 8) {
	const s = String(season || "").trim().toUpperCase();
	const m = s.match(/^(AW|SS)[- ]?(\d{2,4})$/);
	if (!m) return [];
	const type = m[1];
	const rawYear = m[2];
	const year = parseInt(rawYear.length === 4 ? rawYear.slice(-2) : rawYear, 10);
	const sep = s.includes("-") ? "-" : s.includes(" ") ? " " : "";
	const pad = (y) => String(y).padStart(rawYear.length, "0");
	const fallbacks = [];
	let t = type;
	let y = year;
	for (let i = 0; i < max; i++) {
		if (t === "AW") {
			t = "SS"; // SS of same year is immediately before AW of same year
		} else {
			t = "AW";
			y -= 1;
		}
		fallbacks.push(`${t}${sep}${pad(y)}`);
	}
	return fallbacks;
}

function ProductCard({ product, metricsChannel = null, allProducts = [], onNavigateToProduct = null }) {
	const [open, setOpen] = useState(false);
	const [imageLoadError, setImageLoadError] = useState(false);
	const [recsOpen, setRecsOpen] = useState(false);

	const rawImageUrl = product.image_url ?? product.URL;
	const hasValidImage = (() => {
		if (rawImageUrl == null) return false;
		const s = String(rawImageUrl).trim();
		if (s === "" || s === "-" || s === "N/A") return false;
		const lower = s.toLowerCase();
		if (lower === "null" || lower === "undefined") return false;
		return true;
	})();

	/** Tall packshots (common for footwear) + cover/top crop hides the product; use contain + bottom bias when covering */
	const isDenimPants = (() => {
		const sub = String(product.sub_category || "").trim().toUpperCase();
		const cat = String(product.category || "").trim().toUpperCase();
		// category = IM.DEPARTMENT (e.g. "DENIM PANT"); sub_category = IM.SUB_CAT (e.g. "DENIM PANTS")
		return (
			sub === "DENIM PANTS" ||
			cat === "DENIM PANTS" ||
			sub.includes("DENIM PANT") ||
			cat.includes("DENIM PANT")
		);
	})();

	const formatStockCover = () => {
		const cover = product.stock_cover_period;
		if (cover == null || cover === 0 || Number.isNaN(Number(cover))) return "—";
		return `${Number(cover).toFixed(1)} days`;
	};

	const formatRos = (value) => {
		const n = Number(value);
		if (value == null || value === "" || Number.isNaN(n) || n <= 0) return "—";
		return n.toFixed(2);
	};

	const footwearLike = (() => {
		const d = String(product.division || "").toUpperCase();
		const cat = String(product.category || "").toUpperCase();
		const sub = String(product.sub_category || "").toUpperCase();
		return (
			d.includes("FOOT") ||
			cat.includes("SNEAKER") ||
			cat.includes("SHOE") ||
			cat.includes("FOOTWEAR") ||
			cat.includes("SANDAL") ||
			cat.includes("BOOT") ||
			sub.includes("SNEAKER") ||
			sub.includes("SHOE") ||
			sub.includes("FOOTWEAR")
		);
	})();

	/**
	 * Image stage height: mobile keeps clamp+vw; md+ uses aspect-ratio + caps so layout is stable for
	 * html2canvas PDF export (vw/clamp often mis-resolves in the cloned document).
	 */
	const imageStageSx = {
		position: "relative",
		width: "100%",
		...(footwearLike
			? {
					height: {
						xs: "clamp(200px, 58vw, 270px)",
						sm: "clamp(215px, 40vw, 300px)",
						md: "auto",
						lg: "auto",
						xl: "auto",
					},
					aspectRatio: { md: "5 / 6", lg: "5 / 6", xl: "5 / 6" },
					minHeight: { md: 200, lg: 200, xl: 200 },
					maxHeight: { md: 300, lg: 300, xl: 300 },
				}
			: {
					height: {
						xs: "clamp(160px, 50vw, 230px)",
						sm: "clamp(175px, 34vw, 260px)",
						md: "auto",
						lg: "auto",
						xl: "auto",
					},
					aspectRatio: { md: "4 / 5", lg: "4 / 5", xl: "4 / 5" },
					minHeight: { md: 170, lg: 170, xl: 170 },
					maxHeight: { md: 260, lg: 260, xl: 260 },
				}),
	};

	useEffect(() => {
		setImageLoadError(false);
	}, [rawImageUrl, product.PKEY]);

	const showImage = hasValidImage && !imageLoadError;

	const handleOpen = () => {
		if (showImage) setOpen(true);
	};
	const handleClose = () => setOpen(false);

	/** Compact grid; short labels + container columns avoid ellipsis truncation */
	const metricGridCellSx = {
		minWidth: 0,
		maxWidth: "100%",
		width: "100%",
		textAlign: "left",
		pr: { xs: 0.1, sm: 0.2 },
		boxSizing: "border-box",
	};
	const metricLabelTypoSx = {
		fontSize: { xs: "0.45rem", sm: "0.48rem", md: "0.5rem" },
		fontWeight: 600,
		color: "#888",
		textTransform: "uppercase",
		lineHeight: 1.15,
		maxWidth: "100%",
		minWidth: 0,
		whiteSpace: "normal",
		overflowWrap: "break-word",
	};
	const metricValueTypoSx = {
		fontSize: { xs: "0.45rem", sm: "0.48rem", md: "0.5rem" },
		fontWeight: 600,
		color: "#1a1a1a",
		lineHeight: 1.15,
		maxWidth: "100%",
		minWidth: 0,
		fontVariantNumeric: "tabular-nums",
	};
	const metricValueClampSx = {
		...metricValueTypoSx,
		whiteSpace: "nowrap",
		overflow: "hidden",
		textOverflow: "ellipsis",
	};
	/**
	 * Headline KPI band. Eleven tiles only sit on a single line on very wide cards; at the usual
	 * four-across width six columns is the most that keeps values like "45% (120)" un-truncated,
	 * so the band settles into two rows there.
	 */
	const kpiStripSx = {
		display: "grid",
		gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
		"@container (min-width: 280px)": {
			gridTemplateColumns: "repeat(6, minmax(0, 1fr))",
		},
		"@container (min-width: 600px)": {
			gridTemplateColumns: "repeat(11, minmax(0, 1fr))",
		},
		columnGap: 0.3,
		rowGap: 0.4,
		mt: 0.5,
		px: 0.4,
		py: 0.4,
		bgcolor: "#fafafa",
		border: "1px solid #ececec",
		borderRadius: 0.5,
	};
	const kpiCellSx = { minWidth: 0, maxWidth: "100%", textAlign: "center", boxSizing: "border-box" };
	const kpiLabelTypoSx = {
		fontSize: { xs: "0.42rem", sm: "0.45rem", md: "0.47rem" },
		fontWeight: 600,
		color: "#888",
		textTransform: "uppercase",
		lineHeight: 1.15,
		whiteSpace: "nowrap",
		overflow: "hidden",
		textOverflow: "ellipsis",
	};
	const kpiLabelBoldTypoSx = { ...kpiLabelTypoSx, fontWeight: 800, color: "#555" };
	const kpiValueTypoSx = {
		fontSize: { xs: "0.44rem", sm: "0.47rem", md: "0.49rem" },
		fontWeight: 700,
		color: "#1a1a1a",
		lineHeight: 1.2,
		fontVariantNumeric: "tabular-nums",
		whiteSpace: "nowrap",
		overflow: "hidden",
		textOverflow: "ellipsis",
	};

	/** Rupee amounts in the KPI band are shown in lakhs (1L = 1,00,000) to keep the columns narrow. */
	const formatLakhs = (value) => {
		if (value == null || value === "") return "—";
		const n = Number(String(value).replace(/,/g, ""));
		if (Number.isNaN(n)) return "—";
		return `${(n / 100000).toFixed(2)}L`;
	};

	/** Gross Profit per piece = GP Value ÷ Units Sold */
	const formatGpPerPiece = () => {
		const gpValue = parseFloat(String(product.lcogs?.gp_value || "0").replace(/,/g, "")) || 0;
		const unitsSold = parseFloat(product.troco_ls?.t_sale) || 0;
		if (!unitsSold) return "—";
		const val = Math.round(gpValue / unitsSold);
		return val.toLocaleString("en-IN");
	};

	const realisation = getProductRealisation(product);

	/** MRP ÷ COGS (multiple); same inputs as MRP / COGS tiles */
	const formatMrpCogsMultiple = () => {
		const mrp = parseFloat(String(product.troco_ls?.mrp ?? "").replace(/,/g, "")) || 0;
		const cogs = parseFloat(String(product.COGS ?? "").replace(/,/g, "")) || 0;
		if (!cogs) return "—";
		return (mrp / cogs).toFixed(2);
	};

	// Format numbers with commas but no decimals
	const formatCurrency = (value) => {
		if (!value || value === "N/A") return "N/A";
		// Remove any existing commas and handle string "N/A"
		const cleanValue = value.toString().replace(/,/g, "");
		// Parse the number and round it
		const numValue = Math.round(parseFloat(cleanValue));
		// Check if it's a valid number
		if (isNaN(numValue)) return "N/A";
		// Format with commas but no decimals
		return numValue.toLocaleString("en-IN");
	};

	const formatNumber = (num) => {
		if (!num) return "0";
		return Math.round(Number(num)).toLocaleString("en-IN");
	};

	const agingBucket = metricsChannelToBucket(metricsChannel);

	// Min receive-date aging (oldest inward); prefer backend AVG_AGING scoped to channel / store channel
	const calculateAverageAging = () => {
		if (product.avg_aging != null && product.avg_aging !== "" && !Number.isNaN(Number(product.avg_aging))) {
			return Math.round(Number(product.avg_aging));
		}
		const agingValues = [product.ebo_aging || 0, product.lfs_aging || 0, product.mbo_aging || 0, product.ecom_aging || 0].filter(
			(value) => value > 0
		);
		if (agingValues.length === 0) return 0;
		return Math.round(agingValues.reduce((sum, value) => sum + value, 0) / agingValues.length);
	};

	// Max receive-date aging (newest inward)
	const calculateMaxAging = () => {
		if (product.avg_max_aging != null && product.avg_max_aging !== "" && !Number.isNaN(Number(product.avg_max_aging))) {
			return Math.round(Number(product.avg_max_aging));
		}
		const agingValues = [
			product.ebo_max_aging || 0,
			product.lfs_max_aging || 0,
			product.mbo_max_aging || 0,
			product.ecom_max_aging || 0,
		].filter((value) => value > 0);
		if (agingValues.length === 0) return 0;
		return Math.round(agingValues.reduce((sum, value) => sum + value, 0) / agingValues.length);
	};

	const parseInwDate = (value) => {
		if (!value) return null;
		const s = String(value).trim();
		if (!s) return null;
		const ddMmYyyy = /^(\d{2})-(\d{2})-(\d{4})$/;
		const m = s.match(ddMmYyyy);
		if (m) {
			const d = new Date(Number(m[3]), Number(m[2]) - 1, Number(m[1]));
			return Number.isNaN(d.getTime()) ? null : d;
		}
		const d = new Date(s);
		return Number.isNaN(d.getTime()) ? null : d;
	};

	const formatInwDateDisplay = (value) => {
		if (!value) return "—";
		if (typeof value === "string" && value.trim()) return value.trim();
		const d = value instanceof Date ? value : parseInwDate(value);
		if (!d) return "—";
		const day = String(d.getDate()).padStart(2, "0");
		const month = String(d.getMonth() + 1).padStart(2, "0");
		return `${day}-${month}-${d.getFullYear()}`;
	};

	const pickExtremeInwDate = (dates, pickMin) => {
		const parsed = dates.map(parseInwDate).filter(Boolean);
		if (parsed.length === 0) return "—";
		const ts = pickMin
			? Math.min(...parsed.map((d) => d.getTime()))
			: Math.max(...parsed.map((d) => d.getTime()));
		return formatInwDateDisplay(new Date(ts));
	};

	const calculateFirstInwDate = () => {
		if (product.first_inw_date) return formatInwDateDisplay(product.first_inw_date);
		return pickExtremeInwDate(
			[product.ebo_first_inw_date, product.lfs_first_inw_date, product.mbo_first_inw_date, product.ecom_first_inw_date],
			true
		);
	};

	const calculateMaxInwDate = () => {
		if (product.max_inw_date) return formatInwDateDisplay(product.max_inw_date);
		return pickExtremeInwDate(
			[product.ebo_max_inw_date, product.lfs_max_inw_date, product.mbo_max_inw_date, product.ecom_max_inw_date],
			false
		);
	};

	// Calculate total WH SOH across all channels
	const calculateTotalWHSOH = () => {
		return product.total_wh_soh || 0;
	};

	// Calculate total EBO SOH across all channels
	const calculateTotalEBOSOH = () => {
		return product.total_ebo_soh || 0;
	};

	/** WH + EBO inventory for brand-specific pivotal sizes vs other sizes — from API */
	const calculatePivotalInv = () => Math.round(Number(product.inv_pivotal) || 0);
	const calculateNonPivotalInv = () => Math.round(Number(product.inv_non_pivotal) || 0);
	const nonPivotalWhSoh = () => Math.round(Number(product.non_pivotal_wh_soh) || 0);
	const nonPivotalEboSoh = () => Math.round(Number(product.non_pivotal_ebo_soh) || 0);

	const sizeSohRows = Array.isArray(product.size_soh_breakdown) ? product.size_soh_breakdown : [];

	// Determine if this product's pivotal sizes are broken (any pivotal size has 0 WH or 0 EBO)
	const pivotRows = sizeSohRows.filter((r) => r.pivotal || r.is_pivotal);
	const isWhBroken = pivotRows.length > 0 && pivotRows.some((r) => (r.wh_soh || 0) === 0);
	const isEboBroken = pivotRows.length > 0 && pivotRows.some((r) => (r.ebo_soh || 0) === 0);

	const shopifyStatus = String(product.shopify_status || "").trim().toUpperCase();
	const shopifyStatusColor =
		shopifyStatus === "ACTIVE"   ? "rgba(165,214,167,0.9)"  :
		shopifyStatus === "ARCHIVED" ? "rgba(239,154,154,0.88)" :
		shopifyStatus === "DRAFT"    ? "rgba(189,189,189,0.85)"  : null;
	const isCurrentBroken = isWhBroken || isEboBroken;

	// Find similar products with matching attributes + non-broken pivotal, with season fallback
	const recommendations = useMemo(() => {
		if (!isCurrentBroken || !allProducts?.length) return { items: [], fromSeason: null };

		const norm = (v) => String(v || "").toUpperCase().trim();
		const myBrand   = norm(product.brand);
		const myCat     = norm(product.category);
		const mySubCat  = norm(product.sub_category);
		const myColor   = norm(product.color);
		const myPalette = norm(product.palette);

		const isNonBroken = (p) => {
			const altPivRows = Array.isArray(p.size_soh_breakdown)
				? p.size_soh_breakdown.filter((r) => r.pivotal || r.is_pivotal)
				: [];
			if (altPivRows.length === 0) {
				return (p.pivotal_wh_soh || 0) > 0 && (p.pivotal_ebo_soh || 0) > 0;
			}
			return altPivRows.every((r) => (r.wh_soh || 0) > 0 && (r.ebo_soh || 0) > 0);
		};

		const matchesForSeason = (targetSeason) =>
			allProducts
				.filter((p) => {
					if (p.PKEY === product.PKEY) return false;
					if (norm(p.brand)        !== myBrand)   return false;
					if (norm(p.category)     !== myCat)     return false;
					if (norm(p.sub_category) !== mySubCat)  return false;
					if (norm(p.color)        !== myColor)   return false;
					if (norm(p.palette)      !== myPalette) return false;
					if (norm(p.season)       !== norm(targetSeason)) return false;
					return isNonBroken(p);
				})
				.sort((a, b) =>
					((b.pivotal_wh_soh || 0) + (b.pivotal_ebo_soh || 0)) -
					((a.pivotal_wh_soh || 0) + (a.pivotal_ebo_soh || 0))
				)
				.slice(0, 4);

		// Try current season first, then each preceding season in order
		const seasonList = [product.season, ...getSeasonFallbacks(product.season)];
		for (const season of seasonList) {
			if (!season) continue;
			const found = matchesForSeason(season);
			if (found.length > 0) return { items: found, fromSeason: norm(season) };
		}

		return { items: [], fromSeason: null };
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isCurrentBroken, allProducts, product.PKEY, product.brand, product.category,
		product.sub_category, product.color, product.palette, product.season]);

	// Overall DOH from the backend: stock in view ÷ combined lifetime pace of the channels in view.
	// null = stock but no sales in view (unbounded cover), distinct from 0 = out of stock.
	const calculateAverageDOH = () =>
		product.avg_doh == null ? null : Math.round((Number(product.avg_doh) || 0) * 10) / 10;

	// Create tooltip content for DOH details with black and white theme
	const getDOHTooltipContent = () => {
		// One row per stock pool: store stock feeds EBO; WH stock feeds LFS + MBO + ECOM together
		const channels = [
			{ name: "EBO (store stock)", value: product.ebo_doh || 0 },
			{ name: "WH stock (WH-fed channels in view)", value: product.wh_doh || 0 },
		].filter((channel) => channel.value > 0);

		if (channels.length === 0) return "No DOH data available";

		return (
			<Box
				sx={{
					backgroundColor: "#ffffff",
					color: "#000000",
					p: 1.5,
					borderRadius: 1,
					minWidth: 200,
					boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
					border: "1px solid #ffffff",
				}}>
				<Typography
					variant="body2"
					sx={{
						fontWeight: "bold",
						mb: 1.5,
						color: "#000000",
						fontSize: "0.8125rem",
						borderBottom: "1px solid #ffffff",
						pb: 0.5,
					}}>
					DOH by stock pool (stock ÷ lifetime pace):
				</Typography>
				{channels.map((channel, index) => (
					<Box
						key={index}
						sx={{
							display: "flex",
							justifyContent: "space-between",
							alignItems: "center",
							py: 0.3,
							borderBottom: index < channels.length - 1 ? "1px solid #cccccc" : "none",
						}}>
						<Typography
							variant="body2"
							sx={{
								fontSize: "0.75rem",
								color: "#666666",
								fontWeight: 500,
							}}>
							{channel.name}:
						</Typography>
						<Typography
							variant="body2"
							sx={{
								fontSize: "0.75rem",
								color: "#000000",
								fontWeight: "bold",
							}}>
							{channel.value} days
						</Typography>
					</Box>
				))}
			</Box>
		);
	};

	const buildChannelSalesTooltipContent = (title, channels) => {
		const rows = channels.filter((channel) => channel.value > 0);
		if (rows.length === 0) return "No sales data available";

		return (
			<Box
				sx={{
					backgroundColor: "#ffffff",
					color: "#000000",
					p: 1.5,
					borderRadius: 1,
					minWidth: 200,
					boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
					border: "1px solid #ffffff",
				}}>
				<Typography
					variant="body2"
					sx={{
						fontWeight: "bold",
						mb: 1.5,
						color: "#000000",
						fontSize: "0.8125rem",
						borderBottom: "1px solid #ffffff",
						pb: 0.5,
					}}>
					{title}
				</Typography>
				{rows.map((channel, index) => (
					<Box
						key={channel.name}
						sx={{
							display: "flex",
							justifyContent: "space-between",
							alignItems: "center",
							py: 0.3,
							borderBottom: index < rows.length - 1 ? "1px solid #cccccc" : "none",
						}}>
						<Typography
							variant="body2"
							sx={{
								fontSize: "0.75rem",
								color: "#666666",
								fontWeight: 500,
							}}>
							{channel.name}:
						</Typography>
						<Typography
							variant="body2"
							sx={{
								fontSize: "0.75rem",
								color: "#000000",
								fontWeight: "bold",
							}}>
							{formatNumber(channel.value)}
						</Typography>
					</Box>
				))}
			</Box>
		);
	};

	const channelSoldTooltipSlotProps = {
		tooltip: {
			sx: {
				backgroundColor: "transparent",
				border: "none",
				boxShadow: "none",
				"& .MuiTooltip-arrow": {
					color: "#ffffff",
				},
			},
		},
	};

	// T.SALE (period) — channel breakdown
	const getTSaleTooltipContent = () =>
		buildChannelSalesTooltipContent("Channel Sales Breakdown (Period):", [
			{ name: "D2C", value: product.d2c_sold_qty || 0 },
			{ name: "Marketplace", value: product.marketplace_sold_qty || 0 },
			{ name: "LFS", value: product.lfs_sold_qty || 0 },
			{ name: "MBO", value: product.mbo_sold_qty || 0 },
			{ name: "EBO", value: product.ebo_sold_qty || 0 },
		]);

	// Total sold (overall / lifetime) — channel breakdown
	const getTSaleTotalTooltipContent = () =>
		buildChannelSalesTooltipContent("Channel Sales Breakdown (Overall):", [
			{ name: "D2C", value: product.d2c_sold_qty_total || 0 },
			{ name: "Marketplace", value: product.marketplace_sold_qty_total || 0 },
			{ name: "LFS", value: product.lfs_sold_qty_total || 0 },
			{ name: "MBO", value: product.mbo_sold_qty_total || 0 },
			{ name: "EBO", value: product.ebo_sold_qty_total || 0 },
			{ name: "Other", value: product.other_sold_qty_total || 0 },
		]);

	// Size rank for natural display order (numeric → youth → letter)
	// Create tooltip content for SOH details with black and white theme (matching Aging style)
	const getSOHTooltipContent = () => {
		const whSoh = calculateTotalWHSOH();
		const eboSoh = calculateTotalEBOSOH();
		const piv = calculatePivotalInv();
		const nonPiv = calculateNonPivotalInv();
		const npWh = nonPivotalWhSoh();
		const npEbo = nonPivotalEboSoh();

		if (whSoh === 0 && eboSoh === 0 && piv === 0 && nonPiv === 0) return "No SOH data available";

		// Sort sizes in natural display order
		const tableRows = [...sizeSohRows].sort((a, b) =>
			getSizeRank(a.size || a.size_label) - getSizeRank(b.size || b.size_label)
		);

		// Pivotal column range (contiguous block after sort)
		const firstPivIdx = tableRows.findIndex((r) => r.pivotal || r.is_pivotal);
		const lastPivIdx = tableRows.reduce((m, r, i) => (r.pivotal || r.is_pivotal ? i : m), -1);
		const pivTableRows = tableRows.filter((r) => r.pivotal || r.is_pivotal);
		const pivCount = firstPivIdx >= 0 ? lastPivIdx - firstPivIdx + 1 : 0;
		const prePivCount = firstPivIdx >= 0 ? firstPivIdx : 0;
		const postPivCount = firstPivIdx >= 0 ? tableRows.length - lastPivIdx - 1 : 0;

		// Broken = any pivotal size has 0 stock for that SOH source
		const eboBroken = pivTableRows.length > 0 && pivTableRows.some((r) => (r.ebo_soh || 0) === 0);
		const whBroken = pivTableRows.length > 0 && pivTableRows.some((r) => (r.wh_soh || 0) === 0);

		// Build legend labels from actual data (only show entries that exist)
		const alphaPivSizes = tableRows.filter((r) => r.is_alpha_pivotal).map((r) => r.size || r.size_label || "");
		const numPivSizes = tableRows.filter((r) => r.is_num_pivotal).map((r) => r.size || r.size_label || "");
		const alphaLegendLabel = alphaPivSizes.length > 0 ? alphaPivSizes.join("/") : "";
		const numLegendLabel = (() => {
			if (numPivSizes.length === 0) return "";
			if (numPivSizes.length === 1) return numPivSizes[0];
			const nums = numPivSizes.map((s) => parseFloat(s)).filter((n) => !isNaN(n));
			if (nums.length === numPivSizes.length) return `${Math.min(...nums)}–${Math.max(...nums)}`;
			return numPivSizes.join("/");
		})();

		const renderSOHTable = (label, field, broken) => {
			const brokenColor = broken ? "#c62828" : "#2e7d32";
			const pivBg = broken ? "#fff5f5" : "#f5fbf5";
			const pivBorder = broken ? "#ef9a9a" : "#a5d6a7";
			return (
				<Box sx={{ flex: 1, minWidth: 0, overflow: "hidden" }}>
					{/* Title + badge */}
					<Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.25 }}>
						<Typography sx={{ fontSize: "0.68rem", fontWeight: 700, color: "#222" }}>{label}</Typography>
						{pivCount > 0 && (
							<Typography sx={{
								fontSize: "0.56rem", px: 0.5, py: 0.1, borderRadius: 0.5,
								bgcolor: broken ? "#ffebee" : "#e8f5e9",
								color: brokenColor, fontWeight: 700, lineHeight: 1.5,
							}}>
								{broken ? "Broken" : "Non Broken"}
							</Typography>
						)}
					</Box>
					{/* Span header over pivotal columns */}
					{pivCount > 0 && (
						<Box sx={{ display: "flex", width: "100%", mb: "1px" }}>
							{prePivCount > 0 && <Box sx={{ flex: prePivCount }} />}
							<Box sx={{
								flex: pivCount, textAlign: "center",
								bgcolor: broken ? "#ffebee" : "#e8f5e9",
								borderRadius: "3px 3px 0 0", py: 0.15, mx: "1px",
							}}>
								<Typography sx={{ fontSize: "0.56rem", color: brokenColor, fontWeight: 700, lineHeight: 1 }}>
									{broken ? "Broken" : "Non Broken"}
								</Typography>
							</Box>
							{postPivCount > 0 && <Box sx={{ flex: postPivCount }} />}
						</Box>
					)}
					{/* Size header row */}
					<Box sx={{ display: "flex", width: "100%", borderBottom: `1px solid ${pivBorder}` }}>
						{tableRows.map((row, i) => {
							const isPiv = row.pivotal || row.is_pivotal;
							const isAlpha = row.is_alpha_pivotal;
							const isNum = row.is_num_pivotal;
							const leftEdge = isPiv && i === firstPivIdx;
							const rightEdge = isPiv && i === lastPivIdx;
							return (
								<Box key={i} sx={{
									flex: 1, textAlign: "center", py: 0.15,
									bgcolor: isPiv ? pivBg : "transparent",
									...(leftEdge && { borderLeft: `2px solid ${pivBorder}` }),
									...(rightEdge && { borderRight: `2px solid ${pivBorder}` }),
								}}>
									<Typography sx={{
										fontSize: "0.56rem",
										color: isAlpha ? "#1565c0" : isNum ? "#e65100" : isPiv ? "#2e7d32" : "#888",
										fontWeight: isPiv ? 700 : 400,
										lineHeight: 1.2,
									}}>
										{row.size || row.size_label || "?"}
									</Typography>
								</Box>
							);
						})}
					</Box>
					{/* Values row */}
					<Box sx={{ display: "flex", width: "100%" }}>
						{tableRows.map((row, i) => {
							const val = row[field] || 0;
							const isPiv = row.pivotal || row.is_pivotal;
							const leftEdge = isPiv && i === firstPivIdx;
							const rightEdge = isPiv && i === lastPivIdx;
							return (
								<Box key={i} sx={{
									flex: 1, textAlign: "center", py: 0.2,
									bgcolor: isPiv ? pivBg : "transparent",
									...(leftEdge && { borderLeft: `2px solid ${pivBorder}` }),
									...(rightEdge && { borderRight: `2px solid ${pivBorder}` }),
								}}>
									<Typography sx={{
										fontSize: "0.65rem",
										fontVariantNumeric: "tabular-nums",
										fontWeight: isPiv ? 700 : 400,
										color: (val === 0 && isPiv) ? "#c62828" : "#000",
									}}>
										{val}
									</Typography>
								</Box>
							);
						})}
					</Box>
				</Box>
			);
		};

		return (
			<Box
				sx={{
					backgroundColor: "#ffffff",
					color: "#000000",
					p: 1.5,
					borderRadius: 1,
					minWidth: 300,
					maxWidth: 520,
					boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
					border: "1px solid #e0e0e0",
				}}>
				<Typography
					variant="body2"
					sx={{ fontWeight: "bold", mb: 0.75, color: "#000000", fontSize: "0.8125rem", borderBottom: "1px solid #cccccc", pb: 0.5 }}>
					Stock On Hand Details:
				</Typography>
				{/* Totals — same order as tables below: EBO (left) then WH (right) */}
				<Box sx={{ display: "flex", gap: 2, mb: 0.5 }}>
					<Box sx={{ display: "flex", gap: 0.5, alignItems: "center" }}>
						<Typography variant="body2" sx={{ fontSize: "0.7rem", color: "#666", fontWeight: 500 }}>EBO SOH:</Typography>
						<Typography variant="body2" sx={{ fontSize: "0.7rem", color: "#000", fontWeight: 700 }}>{formatNumber(eboSoh)}</Typography>
					</Box>
					<Box sx={{ display: "flex", gap: 0.5, alignItems: "center" }}>
						<Typography variant="body2" sx={{ fontSize: "0.7rem", color: "#666", fontWeight: 500 }}>WH SOH:</Typography>
						<Typography variant="body2" sx={{ fontSize: "0.7rem", color: "#000", fontWeight: 700 }}>{formatNumber(whSoh)}</Typography>
					</Box>
				</Box>

				{/* Size breakdown tables */}
				{tableRows.length > 0 && (
					<Box sx={{ mt: 0.5, pt: 0.5, borderTop: "1px solid #e0e0e0" }}>
						<Box sx={{ display: "flex", gap: 1.5, alignItems: "flex-start" }}>
							{renderSOHTable("EBO SOH", "ebo_soh", eboBroken)}
							<Box sx={{ width: "1px", bgcolor: "#d0d0d0", alignSelf: "stretch" }} />
							{renderSOHTable("WH SOH", "wh_soh", whBroken)}
						</Box>
					{/* Legend — only show entries that actually appear in the data */}
					{(alphaPivSizes.length > 0 || numPivSizes.length > 0) && (
						<Box sx={{ display: "flex", gap: 1.5, mt: 0.5, flexWrap: "wrap" }}>
							{alphaPivSizes.length > 0 && (
								<Typography sx={{ fontSize: "0.56rem", color: "#1565c0" }}>
									■ Alpha pivotal ({alphaLegendLabel})
								</Typography>
							)}
							{numPivSizes.length > 0 && (
								<Typography sx={{ fontSize: "0.56rem", color: "#e65100" }}>
									■ Numeric pivotal ({numLegendLabel})
								</Typography>
							)}
						</Box>
					)}
					</Box>
				)}

				{/* Non-pivotal summary — EBO then WH to match table columns */}
				<Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 0.75, pt: 0.5, borderTop: "1px solid #e0e0e0" }}>
					<Typography sx={{ fontSize: "0.65rem", color: "#888" }}>Non-pivotal EBO · WH</Typography>
					<Typography sx={{ fontSize: "0.65rem", fontWeight: 700, color: "#000" }}>
						{formatNumber(npEbo)} · {formatNumber(npWh)}
					</Typography>
				</Box>
			</Box>
		);
	};

	const buildAgingTooltip = (title, rows) => {
		const channels = rows
			.filter((ch) => ch.value > 0)
			.filter((ch) => !agingBucket || ch.name === agingBucket);

		if (channels.length === 0) return "No aging data available";

		return (
			<Box
				sx={{
					backgroundColor: "#ffffff",
					color: "#000000",
					p: 1.5,
					borderRadius: 1,
					minWidth: 200,
					boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
					border: "1px solid #ffffff",
				}}>
				<Typography
					variant="body2"
					sx={{
						fontWeight: "bold",
						mb: 1.5,
						color: "#000000",
						fontSize: "0.8125rem",
						borderBottom: "1px solid #ffffff",
						pb: 0.5,
					}}>
					{title}
				</Typography>
				{channels.map((channel, index) => (
					<Box
						key={index}
						sx={{
							display: "flex",
							justifyContent: "space-between",
							alignItems: "center",
							py: 0.3,
							borderBottom: index < channels.length - 1 ? "1px solid #cccccc" : "none",
						}}>
						<Typography
							variant="body2"
							sx={{
								fontSize: "0.75rem",
								color: "#666666",
								fontWeight: 500,
							}}>
							{channel.name}:
						</Typography>
						<Typography
							variant="body2"
							sx={{
								fontSize: "0.75rem",
								color: "#000000",
								fontWeight: "bold",
							}}>
							{channel.value} days
						</Typography>
					</Box>
				))}
			</Box>
		);
	};

	const buildInwDateTooltip = (title, rows) => {
		const channels = rows
			.filter((ch) => ch.value && ch.value !== "—")
			.filter((ch) => !agingBucket || ch.name === agingBucket);

		if (channels.length === 0) return "No inward date data available";

		return (
			<Box
				sx={{
					backgroundColor: "#ffffff",
					color: "#000000",
					p: 1.5,
					borderRadius: 1,
					minWidth: 200,
					boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
					border: "1px solid #ffffff",
				}}>
				<Typography
					variant="body2"
					sx={{
						fontWeight: "bold",
						mb: 1.5,
						color: "#000000",
						fontSize: "0.8125rem",
						borderBottom: "1px solid #ffffff",
						pb: 0.5,
					}}>
					{title}
				</Typography>
				{channels.map((channel, index) => (
					<Box
						key={index}
						sx={{
							display: "flex",
							justifyContent: "space-between",
							alignItems: "center",
							py: 0.3,
							borderBottom: index < channels.length - 1 ? "1px solid #cccccc" : "none",
						}}>
						<Typography
							variant="body2"
							sx={{
								fontSize: "0.75rem",
								color: "#666666",
								fontWeight: 500,
							}}>
							{channel.name}:
						</Typography>
						<Typography
							variant="body2"
							sx={{
								fontSize: "0.75rem",
								color: "#000000",
								fontWeight: "bold",
							}}>
							{channel.value}
						</Typography>
					</Box>
				))}
			</Box>
		);
	};

	const getAgingTooltipContent = () =>
		buildAgingTooltip("Channel aging since stock reached the channel (incl. transit offset):", [
			{ name: "EBO", value: product.ebo_aging || 0 },
			{ name: "LFS", value: product.lfs_aging || 0 },
			{ name: "MBO", value: product.mbo_aging || 0 },
			{ name: "ECOM", value: product.ecom_aging || 0 },
		]);

	const getMaxAgingTooltipContent = () =>
		buildAgingTooltip("Channel aging — last inward:", [
			{ name: "EBO", value: product.ebo_max_aging || 0 },
			{ name: "LFS", value: product.lfs_max_aging || 0 },
			{ name: "MBO", value: product.mbo_max_aging || 0 },
			{ name: "ECOM", value: product.ecom_max_aging || 0 },
		]);

	const getFirstInwDateTooltipContent = () =>
		buildInwDateTooltip("Channel-wise first inward date:", [
			{ name: "EBO", value: formatInwDateDisplay(product.ebo_first_inw_date) },
			{ name: "LFS", value: formatInwDateDisplay(product.lfs_first_inw_date) },
			{ name: "MBO", value: formatInwDateDisplay(product.mbo_first_inw_date) },
			{ name: "ECOM", value: formatInwDateDisplay(product.ecom_first_inw_date) },
		]);

	const getMaxInwDateTooltipContent = () =>
		buildInwDateTooltip("Channel-wise last inward date:", [
			{ name: "EBO", value: formatInwDateDisplay(product.ebo_max_inw_date) },
			{ name: "LFS", value: formatInwDateDisplay(product.lfs_max_inw_date) },
			{ name: "MBO", value: formatInwDateDisplay(product.mbo_max_inw_date) },
			{ name: "ECOM", value: formatInwDateDisplay(product.ecom_max_inw_date) },
		]);

	// Channel-level ROS breakdown from the backend (same maths as the headline numbers):
	// "wgt" = lifetime sold ÷ days on sale, "period" = sold in period ÷ days on sale in the period.
	const getChannelRosTooltipContent = (title, kind) => {
		const isPeriod = kind === "period";
		const channels = ["ebo", "lfs", "mbo", "ecom"]
			.map((key) => ({
				name: key.toUpperCase(),
				days: (isPeriod ? product[`${key}_period_days`] : product[`${key}_ros_days`]) || 0,
				sales: (isPeriod ? product[`${key}_sold_qty`] : product[`${key}_sold_qty_total`]) || 0,
				ros: (isPeriod ? product[`${key}_period_ros`] : product[`${key}_wgt_ros`]) || 0,
			}))
			.filter((channel) => channel.days > 0)
			// Under a channel/store filter only the selected channels carry units (works for multi-select)
			.filter((channel) => !agingBucket || channel.sales > 0);

		// Lead with the headline's own maths so the card number reconciles with the rows
		const total = {
			name: "All in view",
			days: (isPeriod ? product.period_live_days : product.ros_days) || 0,
			sales: (isPeriod ? product.troco_ls?.t_sale : product.troco_ls?.t_sale_total) || 0,
			ros: (isPeriod ? product.period_ros : product.rate_of_sale) || 0,
		};
		const rows = total.days > 0 ? [total, ...channels] : channels;
		if (rows.length === 0) return "No ROS data available";

		return (
			<div
				style={{
					backgroundColor: "#ffffff",
					color: "#000000",
					padding: "12px",
					borderRadius: "4px",
					boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
					border: "1px solid #ffffff",
					minWidth: "250px",
					fontFamily: "Inter, sans-serif",
				}}>
				<div
					style={{
						fontWeight: "bold",
						marginBottom: "12px",
						color: "#000000",
						fontSize: "11px",
						borderBottom: "1px solid #e0e0e0",
						paddingBottom: "4px",
					}}>
					{title}
				</div>
				{rows.map((channel, index) => (
					<div
						key={index}
						style={{
							display: "flex",
							justifyContent: "space-between",
							alignItems: "center",
							padding: "4px 0",
							borderBottom: index < rows.length - 1 ? "1px solid #e0e0e0" : "none",
						}}>
						<span
							style={{
								fontSize: "11px",
								color: "#666666",
								fontWeight: 500,
							}}>
							{channel.name}:
						</span>
						<span
							style={{
								fontSize: "11px",
								color: "#000000",
								fontWeight: "bold",
							}}>
							{Number(channel.ros).toFixed(2)} ({channel.sales} units / {channel.days} days)
						</span>
					</div>
				))}
			</div>
		);
	};

	const getROSTooltipContent = () =>
		getChannelRosTooltipContent("Channel-wise Wgt. ROS (lifetime sold ÷ days on sale):", "wgt");
	const getPeriodROSTooltipContent = () =>
		getChannelRosTooltipContent("Channel-wise Period ROS (sold in period ÷ days on sale in period):", "period");

	const rosTooltipSlotProps = {
		tooltip: {
			sx: {
				backgroundColor: "transparent",
				border: "none",
				boxShadow: "none",
				"& .MuiTooltip-arrow": {
					color: "#ffffff",
				},
			},
		},
	};

	return (
		<>
			<Card
				sx={{
					height: "auto",
					display: "flex",
					flexDirection: "column",
					borderRadius: 0,
					boxShadow: "none",
					border: "none",
					containerType: "inline-size",
					"& .MuiCardMedia-root": {
						borderRadius: 0,
					},
				}}>
				<Box
					data-pdf-image-stage=""
					data-pdf-field="image"
					sx={{
						...imageStageSx,
						flexShrink: 0,
						...(showImage
							? {
									overflow: "hidden",
								}
							: {}),
						...(showImage && footwearLike
							? {
									backgroundColor: "#ebebeb",
								}
							: {}),
						...(showImage
							? {
									"&:hover .zoom-icon": {
										opacity: 1,
									},
								}
							: {}),
					}}
				>
			{/* Shopify status badge — top-right overlay on the image */}
			{shopifyStatusColor && (
				<Box data-pdf-hide="" sx={{
					position: "absolute", top: 4, right: 4, zIndex: 2,
					pointerEvents: "none",
				}}>
					<Box sx={{ ...CHIP_CONTENT_SX, bgcolor: shopifyStatusColor }}>
						SHOPIFY: {shopifyStatus}
					</Box>
				</Box>
			)}

			{/* Broken pivotal badge — top-left overlay on the image */}
			{isCurrentBroken && (
				<Box data-pdf-hide="" sx={{
					position: "absolute", top: 4, left: 4, zIndex: 2,
					display: "flex", flexDirection: "column", gap: "2px",
					pointerEvents: "none",
				}}>
					{/* WH broken chip */}
					{isWhBroken && (
						<Box sx={{ ...CHIP_CONTENT_SX, bgcolor: "rgba(144,202,249,0.9)" }}>
							⚠ WH BROKEN
						</Box>
					)}
					{/* EBO broken chip */}
					{isEboBroken && (
						<Box sx={{ ...CHIP_CONTENT_SX, bgcolor: "rgba(239,154,154,0.9)" }}>
							⚠ EBO BROKEN
						</Box>
					)}
				</Box>
			)}
				{showImage ? (
					<>
						<CardMedia
							component="img"
							image={rawImageUrl}
							alt={product.PKEY}
							onClick={handleOpen}
							onError={() => setImageLoadError(true)}
								sx={{
									display: "block",
									width: "100%",
									height: "100%",
									maxWidth: "100%",
									maxHeight: "100%",
									// Footwear: tall canvas + white top — cover + bottom anchor crops empty space, fills tile (no tiny shoe + overlap)
									...(footwearLike
										? {
												objectFit: "cover",
												objectPosition: "center bottom",
											}
										: {
												objectFit: "cover",
												objectPosition: "center center",
											}),
									borderRadius: 0,
									cursor: "pointer",
								}}
							/>
							<IconButton
								className="zoom-icon"
								onClick={handleOpen}
								sx={{
									position: "absolute",
									left: "50%",
									...(footwearLike
										? {
												bottom: 6,
												top: "auto",
												transform: "translateX(-50%)",
											}
										: {
												top: "50%",
												transform: "translate(-50%, -50%)",
											}),
									backgroundColor: "rgba(0, 0, 0, 0.5)",
									color: "white",
									opacity: 0,
									transition: "opacity 0.3s ease",
									"&:hover": {
										backgroundColor: "rgba(0, 0, 0, 0.7)",
									},
								}}>
								<ZoomInIcon />
							</IconButton>
						</>
					) : (
						<Box
							sx={{
								height: "100%",
								width: "100%",
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
								backgroundColor: "#f5f5f5",
								borderBottom: "1px solid #e0e0e0",
								color: "#757575",
								px: 2,
							}}>
							<Typography variant="body2" sx={{ fontWeight: 500, textAlign: "center", fontSize: "0.8125rem" }}>
								{hasValidImage && imageLoadError ? "Image could not be loaded" : "No Image Found"}
							</Typography>
						</Box>
					)}
				</Box>
				<CardContent
					sx={{
						p: 1,
						"&:last-child": { pb: 1 },
						borderRadius: 0,
					}}>
					<Box data-pdf-field="option">
						<Typography
							variant="subtitle2"
							gutterBottom
							sx={{ fontWeight: "bold", mb: 0.5, textAlign: "center", fontSize: "0.75rem" }}>
							OPTION: {product.category ? `${product.category} · ` : ""}{product.PKEY}
							<Box component="span" data-pdf-field="mrp" sx={{ fontWeight: "bold" }}>
								{" "}
								· MRP: {formatCurrency(product.troco_ls?.mrp)}
							</Box>
							{isDenimPants && (
								<Box component="span" data-pdf-field="fit_type" sx={{ fontWeight: "bold" }}>
									{" "}
									· FIT: {product.fitType || "N/A"}
								</Box>
							)}
						</Typography>
					</Box>

					<Box data-pdf-kpi-strip="" sx={kpiStripSx}>
						<Box data-pdf-field="tinw" sx={kpiCellSx} title="Total inward (buy) qty">
							<Typography variant="body2" sx={kpiLabelTypoSx}>
								Inw
							</Typography>
							<Typography variant="body2" sx={kpiValueTypoSx}>
								{formatNumber(product.troco_ls?.t_inw)}
							</Typography>
						</Box>
						<Box data-pdf-field="total_sold" sx={kpiCellSx}>
							<Tooltip
								title={getTSaleTotalTooltipContent()}
								arrow
								placement="top"
								slotProps={channelSoldTooltipSlotProps}>
								<Box sx={{ cursor: "help", minWidth: 0, maxWidth: "100%" }}>
									<Typography variant="body2" sx={kpiLabelBoldTypoSx} title="Lifetime sold qty">
										T.Sold Qty
									</Typography>
									<Typography variant="body2" sx={kpiValueTypoSx}>
										{formatNumber(product.troco_ls?.t_sale_total)}
									</Typography>
								</Box>
							</Tooltip>
						</Box>
						<Box data-pdf-field="sold_in_period" sx={kpiCellSx}>
							<Tooltip
								title={getTSaleTooltipContent()}
								arrow
								placement="top"
								slotProps={channelSoldTooltipSlotProps}>
								<Box sx={{ cursor: "help", minWidth: 0, maxWidth: "100%" }}>
									<Typography variant="body2" sx={kpiLabelBoldTypoSx} title="Sold qty in period">
										Pd.Sold Qty
									</Typography>
									<Typography variant="body2" sx={kpiValueTypoSx}>
										{formatNumber(product.troco_ls?.t_sale)}
									</Typography>
								</Box>
							</Tooltip>
						</Box>
						<Box data-pdf-field="online" sx={kpiCellSx} title="Online share of sold qty">
							<Typography variant="body2" sx={kpiLabelTypoSx}>
								Online
							</Typography>
							<Typography variant="body2" sx={kpiValueTypoSx}>
								{product.lcogs?.oln_cont || "0%"} ({formatNumber(product.online_sold_qty)})
							</Typography>
						</Box>
						<Box data-pdf-field="offline" sx={kpiCellSx} title="Offline share of sold qty">
							<Typography variant="body2" sx={kpiLabelTypoSx}>
								Offline
							</Typography>
							<Typography variant="body2" sx={kpiValueTypoSx}>
								{product.lcogs?.offln_cont || "0%"} ({formatNumber(product.offline_sold_qty)})
							</Typography>
						</Box>
						<Box data-pdf-field="gp_pct" sx={kpiCellSx}>
							<Typography variant="body2" sx={kpiLabelTypoSx}>
								GP%
							</Typography>
							<Typography variant="body2" sx={kpiValueTypoSx}>
								{product.lcogs?.gp_percentage || "0.00%"}
							</Typography>
						</Box>
						<Box data-pdf-field="gp_value" sx={kpiCellSx} title="Gross profit value, in lakhs">
							<Typography variant="body2" sx={kpiLabelTypoSx}>
								GP Val
							</Typography>
							<Typography variant="body2" sx={kpiValueTypoSx}>
								{formatLakhs(product.lcogs?.gp_value)}
							</Typography>
						</Box>
						<Box data-pdf-field="gp_per_piece" sx={kpiCellSx} title="GP Value ÷ units sold in period">
							<Typography variant="body2" sx={kpiLabelTypoSx}>
								GP/Pcs
							</Typography>
							<Typography variant="body2" sx={kpiValueTypoSx}>
								{formatGpPerPiece()}
							</Typography>
						</Box>
						<Box data-pdf-field="asp" sx={kpiCellSx} title="Realised value ÷ units sold in period">
							<Typography variant="body2" sx={kpiLabelTypoSx}>
								ASP
							</Typography>
							<Typography variant="body2" sx={kpiValueTypoSx}>
								{realisation.asp == null ? "—" : formatCurrency(realisation.asp)}
							</Typography>
						</Box>
						<Box data-pdf-field="discount_pct" sx={kpiCellSx} title="(MRP − ASP) ÷ MRP">
							<Typography variant="body2" sx={kpiLabelTypoSx}>
								Disc%
							</Typography>
							<Typography variant="body2" sx={kpiValueTypoSx}>
								{realisation.discountPct == null ? "—" : `${realisation.discountPct.toFixed(2)}%`}
							</Typography>
						</Box>
						<Box
							data-pdf-field="discount_value"
							sx={kpiCellSx}
							title="(MRP × units sold) − realised value, for the period, in lakhs">
							<Typography variant="body2" sx={kpiLabelTypoSx}>
								Disc Val
							</Typography>
							<Typography variant="body2" sx={kpiValueTypoSx}>
								{realisation.discountValue == null ? "—" : formatLakhs(realisation.discountValue)}
							</Typography>
						</Box>
					</Box>

					<Box
						data-pdf-metrics-grid=""
						sx={{
							display: "grid",
							gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
							"@container (min-width: 300px)": {
								gridTemplateColumns: "repeat(5, minmax(0, 1fr))",
							},
							"@container (min-width: 400px)": {
								gridTemplateColumns: "repeat(6, minmax(0, 1fr))",
							},
							rowGap: 0.6,
							columnGap: 0.4,
							maxWidth: "100%",
							alignItems: "start",
							justifyItems: "stretch",
							mt: 0.75,
						}}>
						{/* Buy & cost */}
						<Box data-pdf-field="season" sx={metricGridCellSx}>
							<Typography variant="body2" sx={metricLabelTypoSx}>
								Season:
							</Typography>
							<Typography variant="body2" sx={metricValueTypoSx}>
								{product.season || "N/A"}
							</Typography>
						</Box>
						<Box data-pdf-field="category" sx={metricGridCellSx}>
							<Typography variant="body2" sx={metricLabelTypoSx} title="Category">
								Cat:
							</Typography>
							<Typography variant="body2" sx={metricValueTypoSx}>
								{product.category || "N/A"}
							</Typography>
						</Box>
						<Box data-pdf-field="cogs" sx={metricGridCellSx}>
							<Typography variant="body2" sx={metricLabelTypoSx}>
								COGS:
							</Typography>
							<Typography variant="body2" sx={metricValueTypoSx}>
								{formatCurrency(product.COGS)}
							</Typography>
						</Box>
						<Box data-pdf-field="multi_mrp_cogs" sx={metricGridCellSx} title="MRP ÷ COGS">
							<Typography variant="body2" sx={metricLabelTypoSx}>
								Multi:
							</Typography>
							<Typography
								variant="body2"
								sx={{ ...metricValueTypoSx, fontVariantNumeric: "tabular-nums" }}>
								{formatMrpCogsMultiple()}
							</Typography>
						</Box>

						{/* Sold & sell-through */}
						<Box data-pdf-field="balance" sx={metricGridCellSx}>
							<Typography variant="body2" sx={metricLabelTypoSx}>
								Balance:
							</Typography>
							<Typography variant="body2" sx={metricValueTypoSx}>
								{formatNumber(product.troco_ls?.balance)}
							</Typography>
						</Box>
						<Box data-pdf-field="period_st" sx={metricGridCellSx}>
							<Typography variant="body2" sx={metricLabelTypoSx} title="Sold in period ÷ buy qty">
								Pd ST%:
							</Typography>
							<Typography variant="body2" sx={metricValueTypoSx}>
								{getPeriodStPct(product).toFixed(2)}%
							</Typography>
						</Box>
						<Box data-pdf-field="st" sx={metricGridCellSx}>
							<Typography variant="body2" sx={metricLabelTypoSx} title="Lifetime sell-through">
								Tot ST%:
							</Typography>
							<Typography variant="body2" sx={metricValueTypoSx}>
								{Number(product.troco_ls?.st ?? 0).toFixed(2)}%
							</Typography>
						</Box>

						{/* Inventory & aging */}
						<Box data-pdf-field="soh" sx={metricGridCellSx}>
							<Tooltip
								title={getSOHTooltipContent()}
								arrow
								placement="top"
								slotProps={{
									tooltip: {
										sx: {
											backgroundColor: "transparent",
											border: "none",
											boxShadow: "none",
											"& .MuiTooltip-arrow": {
												color: "#ffffff",
											},
										},
									},
								}}>
								<Box sx={{ cursor: "help", minWidth: 0, maxWidth: "100%" }}>
									<Typography variant="body2" sx={metricLabelTypoSx}>
										SOH:
									</Typography>
									<Typography variant="body2" sx={metricValueTypoSx}>
										{formatNumber(calculateTotalWHSOH() + calculateTotalEBOSOH())}
									</Typography>
								</Box>
							</Tooltip>
						</Box>
						<Box data-pdf-field="avg_aging" sx={metricGridCellSx}>
							<Tooltip
								title={getAgingTooltipContent()}
								arrow
								placement="top"
								slotProps={{
									tooltip: {
										sx: {
											backgroundColor: "transparent",
											border: "none",
											boxShadow: "none",
											"& .MuiTooltip-arrow": {
												color: "#ffffff",
											},
										},
									},
								}}>
								<Box sx={{ cursor: "help", minWidth: 0, maxWidth: "100%" }}>
									<Typography
										variant="body2"
										sx={metricLabelTypoSx}
										title="Days from first inward date to report end date">
										Aging:
									</Typography>
									<Typography variant="body2" sx={metricValueTypoSx}>
										{calculateAverageAging()} days
									</Typography>
								</Box>
							</Tooltip>
						</Box>
						<Box data-pdf-field="max_recv_aging" sx={metricGridCellSx}>
							<Tooltip
								title={getMaxAgingTooltipContent()}
								arrow
								placement="top"
								slotProps={{
									tooltip: {
										sx: {
											backgroundColor: "transparent",
											border: "none",
											boxShadow: "none",
											"& .MuiTooltip-arrow": {
												color: "#ffffff",
											},
										},
									},
								}}>
								<Box sx={{ cursor: "help", minWidth: 0, maxWidth: "100%" }}>
									<Typography
										variant="body2"
										sx={metricLabelTypoSx}
										title="Days from last inward date to report end date">
										Ag Last:
									</Typography>
									<Typography variant="body2" sx={metricValueTypoSx}>
										{calculateMaxAging()} days
									</Typography>
								</Box>
							</Tooltip>
						</Box>
						<Box data-pdf-field="first_inw_date" sx={metricGridCellSx}>
							<Tooltip
								title={getFirstInwDateTooltipContent()}
								arrow
								placement="top"
								slotProps={{
									tooltip: {
										sx: {
											backgroundColor: "transparent",
											border: "none",
											boxShadow: "none",
											"& .MuiTooltip-arrow": {
												color: "#ffffff",
											},
										},
									},
								}}>
								<Box sx={{ cursor: "help", minWidth: 0, maxWidth: "100%" }}>
									<Typography variant="body2" sx={metricLabelTypoSx} title="Earliest inward receive date">
										1st Inw:
									</Typography>
									<Typography variant="body2" sx={metricValueTypoSx}>
										{calculateFirstInwDate()}
									</Typography>
								</Box>
							</Tooltip>
						</Box>
						<Box data-pdf-field="max_inw_date" sx={metricGridCellSx}>
							<Tooltip
								title={getMaxInwDateTooltipContent()}
								arrow
								placement="top"
								slotProps={{
									tooltip: {
										sx: {
											backgroundColor: "transparent",
											border: "none",
											boxShadow: "none",
											"& .MuiTooltip-arrow": {
												color: "#ffffff",
											},
										},
									},
								}}>
								<Box sx={{ cursor: "help", minWidth: 0, maxWidth: "100%" }}>
									<Typography variant="body2" sx={metricLabelTypoSx} title="Last inward receive date">
										Last Inw:
									</Typography>
									<Typography variant="body2" sx={metricValueTypoSx}>
										{calculateMaxInwDate()}
									</Typography>
								</Box>
							</Tooltip>
						</Box>

						{/* Velocity & attributes */}
						<Box data-pdf-field="avg_doh" sx={metricGridCellSx}>
							<Tooltip
								title={getDOHTooltipContent()}
								arrow
								placement="top"
								slotProps={{
									tooltip: {
										sx: {
											backgroundColor: "transparent",
											border: "none",
											boxShadow: "none",
											"& .MuiTooltip-arrow": {
												color: "#ffffff",
											},
										},
									},
								}}>
								<Box sx={{ cursor: "help", minWidth: 0, maxWidth: "100%" }}>
									<Typography variant="body2" sx={metricLabelTypoSx}>
										DOH:
									</Typography>
									<Typography variant="body2" sx={metricValueTypoSx}>
										{calculateAverageDOH() == null ? "No sales" : `${calculateAverageDOH()} days`}
									</Typography>
								</Box>
							</Tooltip>
						</Box>
						<Box data-pdf-field="period_ros" sx={metricGridCellSx}>
							<Tooltip
								title={getPeriodROSTooltipContent()}
								arrow
								placement="top"
								slotProps={rosTooltipSlotProps}>
								<Box sx={{ cursor: "help", minWidth: 0, maxWidth: "100%" }}>
									<Typography variant="body2" sx={metricLabelTypoSx} title="Sold in period ÷ days on sale in the period">
										Pd ROS:
									</Typography>
									<Typography variant="body2" sx={metricValueTypoSx}>
										{formatRos(product.period_ros)}
									</Typography>
								</Box>
							</Tooltip>
						</Box>
						<Box data-pdf-field="stock_cover_period" sx={metricGridCellSx}>
							<Typography variant="body2" sx={metricLabelTypoSx} title="SOH ÷ period ROS">
								Stock Cover:
							</Typography>
							<Typography variant="body2" sx={metricValueTypoSx}>
								{formatStockCover()}
							</Typography>
						</Box>
						<Box data-pdf-field="ros" sx={metricGridCellSx}>
							<Tooltip
								title={getROSTooltipContent()}
								arrow
								placement="top"
								slotProps={rosTooltipSlotProps}>
								<Box sx={{ cursor: "help", minWidth: 0, maxWidth: "100%" }}>
									<Typography
										variant="body2"
										sx={metricLabelTypoSx}
										title="Lifetime sold ÷ days on sale (since stock first reached a channel in view, or its first sale if earlier)">
										Wgt. ROS:
									</Typography>
									<Typography variant="body2" sx={metricValueTypoSx}>
										{formatRos(product.rate_of_sale)}
									</Typography>
								</Box>
							</Tooltip>
						</Box>
						<Box data-pdf-field="blend" sx={metricGridCellSx}>
							<Typography variant="body2" sx={metricLabelTypoSx}>
								Blend:
							</Typography>
							<Typography
								variant="body2"
								sx={metricValueClampSx}
								title={product.blend || product.fabricBlends || "N/A"}>
								{product.blend || product.fabricBlends || "N/A"}
							</Typography>
						</Box>
						<Box data-pdf-field="palette_type" sx={metricGridCellSx}>
							<Typography variant="body2" sx={metricLabelTypoSx}>
								Palette:
							</Typography>
							<Typography
								variant="body2"
								sx={metricValueClampSx}
								title={product.paletteType || product.palette || "N/A"}>
								{product.paletteType || product.palette || "N/A"}
							</Typography>
						</Box>
					</Box>

				{/* Recommendations — shown only when pivotal sizes are broken */}
				{isCurrentBroken && recommendations.items.length > 0 && (
					<Box sx={{ mt: 0.75, pt: 0.5, borderTop: "2px solid #ffcc80" }}>
						<Box
							onClick={() => setRecsOpen((prev) => !prev)}
							sx={{
								display: "flex", alignItems: "center", justifyContent: "space-between",
								cursor: "pointer", mb: recsOpen ? 0.5 : 0,
							}}>
							<Box>
								<Typography sx={{ fontSize: "0.58rem", color: "#e65100", fontWeight: 700, lineHeight: 1.3 }}>
									Similar Options — Full Pivotal Stock ({recommendations.items.length})
								</Typography>
								{recommendations.fromSeason && recommendations.fromSeason !== String(product.season || "").toUpperCase().trim() && (
									<Typography sx={{ fontSize: "0.5rem", color: "#888", lineHeight: 1.2 }}>
										from season: {recommendations.fromSeason}
									</Typography>
								)}
							</Box>
							<Typography sx={{ fontSize: "0.58rem", color: "#e65100", ml: 0.5, flexShrink: 0 }}>
								{recsOpen ? "▲" : "▼"}
							</Typography>
						</Box>
						{recsOpen && recommendations.items.map((rec, idx) => {
							const recImg = rec.image_url ?? rec.URL;
							const hasRecImg = recImg && !["", "-", "N/A", "null", "undefined"].includes(String(recImg).trim().toLowerCase());
							const recPivTotal = (rec.pivotal_wh_soh || 0) + (rec.pivotal_ebo_soh || 0);
							return (
								<Box
									key={rec.PKEY}
									onClick={() => onNavigateToProduct ? onNavigateToProduct(rec.PKEY) : scrollToProductCard(rec.PKEY)}
									sx={{
										display: "flex", alignItems: "center", gap: 0.75,
										py: 0.4, px: 0.25,
										borderBottom: idx < recommendations.items.length - 1 ? "1px solid #f5f5f5" : "none",
										bgcolor: idx % 2 === 0 ? "#fffde7" : "#fff8e1",
										borderRadius: 0.5,
										cursor: "pointer",
										transition: "background-color 0.15s",
										"&:hover": { bgcolor: "#ffe082" },
									}}
									title="Click to jump to this option"
								>
									{/* Thumbnail */}
									<Box sx={{
										width: 36, height: 36, flexShrink: 0,
										border: "1px solid #e0e0e0", borderRadius: 0.5,
										overflow: "hidden", bgcolor: "#fafafa",
									}}>
										{hasRecImg ? (
											<img
												src={getProductImageThumbUrl(recImg, 72) || recImg}
												alt={rec.PKEY}
												loading="lazy"
												decoding="async"
												onError={(e) => {
													// Sized variant can 404 for already-suffixed Shopify URLs — fall back to the raw asset
													if (e.currentTarget.src !== recImg) e.currentTarget.src = recImg;
												}}
												style={{ width: "100%", height: "100%", objectFit: "cover" }}
											/>
										) : (
											<Box sx={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
												<Typography sx={{ fontSize: "0.38rem", color: "#bbb" }}>N/A</Typography>
											</Box>
										)}
									</Box>
									{/* Style / color / season */}
									<Box sx={{ flex: 1, minWidth: 0 }}>
										<Typography sx={{ fontSize: "0.55rem", fontWeight: 700, color: "#333", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
											{rec.style || rec.PKEY}
										</Typography>
										<Typography sx={{ fontSize: "0.5rem", color: "#666", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
											{[rec.color, rec.palette].filter(Boolean).join(" · ")}
										</Typography>
										{rec.season && String(rec.season).toUpperCase() !== recommendations.fromSeason && (
											<Typography sx={{ fontSize: "0.46rem", color: "#aaa" }}>{rec.season}</Typography>
										)}
									</Box>
									{/* Pivotal SOH + jump arrow */}
									<Box sx={{ flexShrink: 0, textAlign: "right" }}>
										<Typography sx={{ fontSize: "0.56rem", color: "#2e7d32", fontWeight: 700, fontVariantNumeric: "tabular-nums" }}>
											{recPivTotal}
										</Typography>
										<Typography sx={{ fontSize: "0.44rem", color: "#888" }}>
											{rec.pivotal_wh_soh || 0}W · {rec.pivotal_ebo_soh || 0}E
										</Typography>
									</Box>
									<Typography sx={{ fontSize: "0.6rem", color: "#bbb", flexShrink: 0, ml: 0.25 }}>↗</Typography>
								</Box>
							);
						})}
					</Box>
				)}
			</CardContent>
		</Card>

		{/* Zoom Modal */}
			<Modal
				open={open}
				onClose={handleClose}
				aria-labelledby="image-modal"
				sx={{
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
					"& .MuiBackdrop-root": {
						backgroundColor: "rgba(0, 0, 0, 0.9)",
					},
				}}>
				<Box
					sx={{
						maxWidth: "90vw",
						maxHeight: "90vh",
						outline: "none",
						bgcolor: "background.paper",
						p: 1,
					}}
					onClick={handleClose}>
					{showImage ? (
						<img
							src={rawImageUrl}
							alt={product.PKEY}
							style={{
								width: "100%",
								height: "100%",
								maxWidth: "100%",
								maxHeight: "90vh",
								objectFit: "contain",
								objectPosition: footwearLike ? "center bottom" : "center center",
							}}
						/>
					) : (
						<Typography sx={{ p: 4, color: "text.secondary" }}>No Image Found</Typography>
					)}
				</Box>
			</Modal>
		</>
	);
}

export default memo(ProductCard);
